import ClassFilterSelect, { ClassFilterOption } from '@/components/ClassFilterSelect';
import { InsightsChat } from '@/components/InsightsChat';
import ContentLayout from '@/components/layout/ContentLayout';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig, ChartContainer } from '@/components/ui/chart';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ReadonlyQuestionDisplay } from '@/components/readonly-question-display';
import { useAuth } from '@/contexts/AuthContext';
import { cn, getQuestionText } from '@/lib/utils';
import { getAllClassInfo, getAssignmentListByTeacherV2, getAssignmentStatsByTeacherV2, getQuestionInfo, getStudentAssignmentStats } from '@/services/generatedAPIs';
import { AssignmentModel, AssignmentStats, QuestionInfo, QuestionStats, StudentAssignmentStat } from '@/services/generatedAPIs.schemas';
import { isEN } from '@/utils/i18n';
import { addDays, format, isAfter, isBefore, subDays } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { zhHK } from 'date-fns/locale/zh-HK';
import { CalendarIcon } from 'lucide-react';
import React from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import type { TooltipProps } from 'recharts';

const THEME_PRIMARY = "var(--color-primary)";
const COMPLETED_COLOR = THEME_PRIMARY;
const NOT_COMPLETED_COLOR = "var(--color-chart-5)";
const CORRECTNESS_LINE_COLOR = THEME_PRIMARY;
const CORRECTNESS_PASS_DOT_COLOR = 'var(--color-chart-2)';
const CORRECTNESS_FAIL_COLOR = 'var(--color-chart-1)';
const NA_POINT_FILL = '#E5E7EB';
const NA_POINT_STROKE = '#9CA3AF';

const buildAssignmentCacheKey = (assignmentId?: number | null, classId?: number | null) => {
    if (!assignmentId || !classId) {
        return null;
    }
    return `${assignmentId}-${classId}`;
};

const buildAssignmentGroupKey = (assignment: AssignmentModel['assignment']) => {
    if (!assignment) {
        return null;
    }
    const normalizedTitle = assignment.title?.trim().toLowerCase() ?? 'untitled';
    const dueDateValue = assignment.dueDate ? new Date(assignment.dueDate).getTime() : 'unknown';
    const teacherValue = assignment.teacherId ?? 'unknown';

    return `${teacherValue}-${normalizedTitle}-${dueDateValue}`;
};

type AssignmentRecord = {
    model: AssignmentModel;
    stats: AssignmentStats;
};

type AssignmentRangeEntry = {
    key: string;
    groupKey: string;
    assignmentId: number;
    classId: number;
    dueDate: number;
    assignmentName: string;
};

type AssignmentPerformanceDatum = {
    groupKey: string;
    assignmentName: string;
    dueDate: number;
    completed: number;
    notCompleted: number;
    total: number;
    correctRate: number | null;
    correctRateValue: number;
    hasCompletedSubmissions: boolean;
    solidLineValue: number | null;
    dottedLineValue: number | null;
    naPointValue: number | null;
    submissionDotValue: number | null;
};

type AssignmentAxisTickProps = {
    x?: number;
    y?: number;
    payload?: {
        value?: string;
    };
};

type CorrectnessDotProps = {
    cx?: number;
    cy?: number;
    payload?: AssignmentPerformanceDatum;
    active?: boolean;
};

type RechartsDotProps = CorrectnessDotProps & { key?: React.Key };

type KnowledgePointMistakeStat = {
    name: string;
    occurrences: number;
    incorrect: number;
    percentage: number;
};

type QuestionMistakeStat = {
    key: string;
    questionInfoId?: number | null;
    fallbackLabel: string;
    occurrences: number;
    incorrect: number;
    percentage: number;
    responses: QuestionResponseDetail[];
};

type QuestionTextResolution = {
    preview: string;
    fullText: string;
    status: 'loading' | 'ready' | 'empty' | 'missing';
};

type QuestionResponseDetail = {
    id: string;
    studentName: string;
    answerText: string;
    isCorrect: boolean;
};

const normalizeRichText = (input?: string | null) => {
    if (!input) {
        return '';
    }
    return input.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
};

const buildPreviewText = (text: string) => {
    if (!text) {
        return '';
    }

    const hasCjk = /[\u3000-\u9fff]/.test(text);
    if (hasCjk) {
        const limit = 25;
        return text.length > limit ? `${text.slice(0, limit)}…` : text;
    }

    const words = text.split(/\s+/).filter(Boolean);
    if (words.length > 1) {
        return words.length > 10 ? `${words.slice(0, 10).join(' ')}…` : text;
    }

    const limit = 40;
    return text.length > limit ? `${text.slice(0, limit)}…` : text;
};

const AssignmentAxisTick = ({ x = 0, y = 0, payload }: AssignmentAxisTickProps) => {
    const label = payload?.value ?? '';
    const shouldRotate = typeof label === 'string' && label.length > 12;
    const textY = y + 12;

    if (shouldRotate) {
        return (
            <g transform={`translate(${x},${textY})`}>
                <text textAnchor="end" transform="rotate(-45)" className="fill-muted-foreground text-[10px]">
                    {label}
                </text>
            </g>
        );
    }

    return (
        <text x={x} y={textY} textAnchor="middle" className="fill-muted-foreground text-xs">
            {label}
        </text>
    );
};

const AssignmentCorrectnessDot = ({ cx, cy, payload, active }: CorrectnessDotProps) => {
    if (typeof cx !== 'number' || typeof cy !== 'number' || !payload) {
        return null;
    }

    const radius = active ? 7 : 5;
    const strokeWidth = active ? 3 : 2;

    if (!payload.hasCompletedSubmissions) {
        return (
            <circle
                cx={cx}
                cy={cy}
                r={radius}
                fill={NA_POINT_FILL}
                stroke={NA_POINT_STROKE}
                strokeWidth={strokeWidth}
                strokeDasharray="2 2"
            />
        );
    }

    const resolvedFill = payload.correctRateValue >= 60 ? CORRECTNESS_PASS_DOT_COLOR : CORRECTNESS_FAIL_COLOR;

    return <circle cx={cx} cy={cy} r={radius} fill={resolvedFill} stroke="#fff" strokeWidth={strokeWidth} />;
};

const renderDot = (props: RechartsDotProps) => {
    const { key, ...rest } = props;
    return <AssignmentCorrectnessDot key={key} {...rest} />;
};

const OverviewPage = () => {
    const { t, i18n } = useTranslation();
    const { getCurrentUser } = useAuth();

    const chartConfig = {
        assignmentCorrectRate: {
            label: t('teacher.correctRate'),
            color: CORRECTNESS_LINE_COLOR,
        },
        assignmentCompleted: {
            label: t('common.completed'),
            color: COMPLETED_COLOR,
        },
        assignmentNotCompleted: {
            label: t('common.notCompleted'),
            color: NOT_COMPLETED_COLOR,
        },
        assignmentMissing: {
            color: NA_POINT_STROKE
        }
    } satisfies ChartConfig;

    const [date, setDate] = React.useState<DateRange>({
        from: subDays(new Date(), 7),
        to: addDays(new Date(), 7),
    });
    const [classOptions, setClassOptions] = React.useState<ClassFilterOption[]>([]);
    const [selectedClassId, setSelectedClassId] = React.useState<string>('all');
    const [selectedSource, setSelectedSource] = React.useState<string>('all');
    const [assignmentRecords, setAssignmentRecords] = React.useState<AssignmentRecord[]>([]);
    const [isAssignmentListLoading, setIsAssignmentListLoading] = React.useState(false);
    const [assignmentStatsCache, setAssignmentStatsCache] = React.useState<Record<string, StudentAssignmentStat[]>>({});
    const [assignmentQuestionStatsCache, setAssignmentQuestionStatsCache] = React.useState<Record<string, QuestionStats[]>>({});
    const [isQuestionStatsLoading, setIsQuestionStatsLoading] = React.useState(false);
    const [questionInfoCache, setQuestionInfoCache] = React.useState<Record<number, QuestionInfo>>({});
    const [dialogQuestionKey, setDialogQuestionKey] = React.useState<string | null>(null);
    const assignmentStatsFetchQueue = React.useRef<Set<string>>(new Set());
    const questionInfoFetchQueue = React.useRef<Set<number>>(new Set());
    
    const handleStartDateSelect = (selected: Date | undefined) => {
        if (!selected) {
            return;
        }

        setDate((prev) => {
            const nextTo = prev?.to && isBefore(selected, prev.to) ? prev.to : selected;
            return {
                from: selected,
                to: nextTo,
            };
        });
    };

    const handleEndDateSelect = (selected: Date | undefined) => {
        if (!selected) {
            return;
        }

        setDate((prev) => {
            const currentFrom = prev?.from ?? selected;
            const normalizedFrom = isAfter(currentFrom, selected) ? selected : currentFrom;
            return {
                from: normalizedFrom,
                to: selected,
            };
        });
    };

    React.useEffect(() => {
        const fetchClassInfo = async () => {
            try {
                const rsp = await getAllClassInfo({ schoolName: getCurrentUser()!.schoolName });
                if (rsp.code === 200 && rsp.data) {
                    const options = rsp.data.classroomInfoResponseList.map((classInfo) => ({
                        id: classInfo.id,
                        name: classInfo.name,
                    }));
                    setClassOptions(options);
                }
            } catch (error) {
                console.error('Error fetching class info', error);
            }
        };

        fetchClassInfo();
    }, [getCurrentUser]);

    React.useEffect(() => {
        if (!classOptions.length) {
            return;
        }

        setSelectedClassId((prev) => {
            if (prev === 'all') {
                return prev;
            }
            const exists = classOptions.some((option) => option.id.toString() === prev);
            if (exists) {
                return prev;
            }
            return 'all';
        });
    }, [classOptions]);

    React.useEffect(() => {
        const fetchAssignments = async () => {
            setIsAssignmentListLoading(true);
            try {
                const rsp = await getAssignmentListByTeacherV2(selectedSource === 'all' ? {} : { source: selectedSource });
                if (rsp.code === 200 && rsp.data) {
                    const assignmentsWithStats: AssignmentRecord[] = rsp.data.assignmentList.map((assignmentModel, index) => {
                        const stats = rsp.data!.assignmentStatsList[index] ?? {
                            totalCount: 0,
                            submittedCount: 0,
                            correctCount: 0,
                            errorKnowledgePoints: [],
                        };
                        return {
                            model: assignmentModel,
                            stats,
                        };
                    });
                    setAssignmentRecords(assignmentsWithStats);
                }
            } catch (error) {
                console.error('Error fetching assignment list by teacher', error);
            } finally {
                setIsAssignmentListLoading(false);
            }
        };

        fetchAssignments();
    }, [getCurrentUser, selectedSource]);

    React.useEffect(() => {
        const fetchAssignmentQuestionStats = async () => {
            setIsQuestionStatsLoading(true);
            try {
                const rsp = await getAssignmentStatsByTeacherV2(selectedSource === 'all' ? {} : { source: selectedSource });
                if (rsp.code === 200 && rsp.data) {
                    const map: Record<string, QuestionStats[]> = {};
                    rsp.data.assignmentList.forEach((assignmentModel, index) => {
                        const assignmentId = assignmentModel.assignment?.id;
                        const classId = assignmentModel.classInfo?.id;
                        const key = buildAssignmentCacheKey(assignmentId, classId);
                        const statsEntry = rsp.data!.assignmentStatsList[index];
                        if (key && statsEntry?.questionStats?.length) {
                            map[key] = statsEntry.questionStats;
                        }
                    });
                    setAssignmentQuestionStatsCache(map);
                }
            } catch (error) {
                console.error('Error fetching assignment stats by teacher', error);
            } finally {
                setIsQuestionStatsLoading(false);
            }
        };

        fetchAssignmentQuestionStats();
    }, [getCurrentUser, selectedSource]);

    const assignmentsInRange = React.useMemo(() => {
        if (!assignmentRecords.length) {
            return [] as AssignmentRangeEntry[];
        }

        const fallbackStart = subDays(new Date(), 7);
        const fallbackEnd = addDays(new Date(), 7);
        const rangeStartDate = date.from ?? fallbackStart;
        const rangeEndDate = date.to ?? fallbackEnd;
        const rangeStart = Math.min(rangeStartDate.getTime(), rangeEndDate.getTime());
        const rangeEnd = Math.max(rangeStartDate.getTime(), rangeEndDate.getTime());

        return assignmentRecords
            .filter(({ model }) => {
                const dueDateString = model.assignment?.dueDate;
                if (!dueDateString) {
                    return false;
                }
                const dueTime = new Date(dueDateString).getTime();
                return dueTime >= rangeStart && dueTime <= rangeEnd;
            })
            .filter(({ model }) => {
                if (selectedClassId === 'all') {
                    return true;
                }
                return model.classInfo?.id?.toString() === selectedClassId;
            })
            .map(({ model }) => {
                const assignmentId = model.assignment?.id;
                const classId = model.classInfo?.id;
                const dueDateString = model.assignment?.dueDate;
                const cacheKey = buildAssignmentCacheKey(assignmentId, classId);
                const groupKey = buildAssignmentGroupKey(model.assignment);
                if (!assignmentId || !classId || !dueDateString || !cacheKey || !groupKey) {
                    return null;
                }
                return {
                    key: cacheKey,
                    groupKey,
                    assignmentId,
                    classId,
                    dueDate: new Date(dueDateString).getTime(),
                    assignmentName: model.assignment?.title?.trim() || t('teacher.untitledAssignment', { defaultValue: 'Untitled assignment' }),
                } satisfies AssignmentRangeEntry;
            })
            .filter((entry): entry is AssignmentRangeEntry => Boolean(entry));
    }, [assignmentRecords, date, selectedClassId, t]);

    React.useEffect(() => {
        if (!assignmentsInRange.length) {
            return;
        }

        const pendingEntries = assignmentsInRange.filter((entry) => {
            if (assignmentStatsCache[entry.key]) {
                return false;
            }
            if (assignmentStatsFetchQueue.current.has(entry.key)) {
                return false;
            }
            return true;
        });

        if (!pendingEntries.length) {
            return;
        }

        pendingEntries.forEach((entry) => {
            assignmentStatsFetchQueue.current.add(entry.key);
            getStudentAssignmentStats({
                assignmentId: entry.assignmentId,
                classInfoId: entry.classId,
            })
                .then((rsp) => {
                    if (rsp.code === 200 && rsp.data) {
                        setAssignmentStatsCache((prev) => ({
                            ...prev,
                            [entry.key]: rsp.data.studentAssignmentStatList,
                        }));
                    }
                })
                .catch((error) => {
                    console.error('Error fetching student assignment stats', error);
                })
                .finally(() => {
                    assignmentStatsFetchQueue.current.delete(entry.key);
                });
        });
    }, [assignmentsInRange, assignmentStatsCache]);

    const assignmentPerformance = React.useMemo(() => {
        if (!assignmentsInRange.length) {
            return {
                data: [] as AssignmentPerformanceDatum[],
                latest: null as AssignmentPerformanceDatum | null,
            };
        }

        const aggregated = new Map<
            string,
            {
                groupKey: string;
                assignmentName: string;
                dueDate: number;
                completed: number;
                totalStudents: number;
                accuracySum: number;
                accuracyCount: number;
            }
        >();

        assignmentsInRange.forEach((entry) => {
            const questionStats = assignmentQuestionStatsCache[entry.key];
            if (!questionStats || !questionStats.length) {
                return;
            }

            // Group QuestionStats by studentId
            const studentStatsMap = new Map<number, { completed: number; correct: number; total: number }>();
            questionStats.forEach((qs) => {
                if (qs.studentId != null) {
                    const existing = studentStatsMap.get(qs.studentId) ?? { completed: 0, correct: 0, total: 0 };
                    existing.total += 1;
                    if (qs.isCompleted === true) {
                        existing.completed += 1;
                    }
                    if (qs.isFirstAttemptCorrect === true) {
                        existing.correct += 1;
                    }
                    studentStatsMap.set(qs.studentId, existing);
                }
            });

            // Determine expected question count (max questions per student)
            const expectedQuestionCount = Math.max(...Array.from(studentStatsMap.values()).map(s => s.total), 0);

            let completedStudents = 0;
            let accuracySum = 0;
            let accuracyCount = 0;
            const totalStudents = studentStatsMap.size;

            studentStatsMap.forEach((studentStats) => {
                // A student is fully completed if they have entries for all expected questions and all are completed
                const fullyCompleted = studentStats.total === expectedQuestionCount &&
                    studentStats.completed === expectedQuestionCount &&
                    expectedQuestionCount > 0;

                if (fullyCompleted) {
                    completedStudents += 1;
                    if (studentStats.completed > 0) {
                        accuracySum += studentStats.correct / studentStats.completed;
                        accuracyCount += 1;
                    }
                }
            });

            const existing = aggregated.get(entry.groupKey);
            if (existing) {
                existing.completed += completedStudents;
                existing.totalStudents += totalStudents;
                existing.accuracySum += accuracySum;
                existing.accuracyCount += accuracyCount;
                existing.dueDate = Math.max(existing.dueDate, entry.dueDate);
            } else {
                aggregated.set(entry.groupKey, {
                    groupKey: entry.groupKey,
                    assignmentName: entry.assignmentName,
                    dueDate: entry.dueDate,
                    completed: completedStudents,
                    totalStudents,
                    accuracySum,
                    accuracyCount,
                });
            }
        });

        const sorted = Array.from(aggregated.values()).sort((a, b) => a.dueDate - b.dueDate);

        const data = sorted.map((entry) => {
            const hasCompletedSubmissions = entry.accuracyCount > 0;
            const averagedAccuracy = hasCompletedSubmissions ? (entry.accuracySum / entry.accuracyCount) * 100 : null;
            return {
                groupKey: entry.groupKey,
                assignmentName: entry.assignmentName,
                dueDate: entry.dueDate,
                completed: entry.completed,
                notCompleted: Math.max(entry.totalStudents - entry.completed, 0),
                total: entry.totalStudents,
                correctRate: averagedAccuracy,
                correctRateValue: averagedAccuracy ?? 0,
                hasCompletedSubmissions,
                solidLineValue: hasCompletedSubmissions ? averagedAccuracy ?? 0 : null,
                dottedLineValue: null,
                naPointValue: hasCompletedSubmissions ? null : 0,
                submissionDotValue: hasCompletedSubmissions ? averagedAccuracy ?? 0 : null,
            } satisfies AssignmentPerformanceDatum;
        });

        data.forEach((entry, index) => {
            const prev = data[index - 1];
            const next = data[index + 1];
            const isAdjacentToNA =
                !entry.hasCompletedSubmissions ||
                (prev && !prev.hasCompletedSubmissions) ||
                (next && !next.hasCompletedSubmissions);

            if (isAdjacentToNA) {
                entry.dottedLineValue = entry.correctRateValue;
            }
        });

        return {
            data,
            latest: data.length ? data[data.length - 1] : null,
        };
    }, [assignmentsInRange, assignmentQuestionStatsCache]);

    const assignmentPerformanceData = assignmentPerformance.data;
    const latestAssignmentPerformance = assignmentPerformance.latest;
    const renderCorrectnessTooltip = React.useCallback(
        (props: TooltipProps<number, string>) => {
            const { active, payload } = props;
            if (!active || !payload?.length) {
                return null;
            }
            const prioritized =
                payload.find((item) => item.dataKey === 'submissionDotValue') ??
                payload.find((item) => item.dataKey === 'solidLineValue') ??
                payload.find((item) => item.dataKey === 'naPointValue');
            if (!prioritized) {
                return null;
            }

            const datum = prioritized.payload as AssignmentPerformanceDatum | undefined;
            if (!datum) {
                return null;
            }

            const isNA = prioritized.dataKey === 'naPointValue' || !datum.hasCompletedSubmissions;
            const valueLabel = isNA ? 'N/A' : `${(datum.correctRateValue ?? 0).toFixed(1)}%`;
            const dueDateLabel = format(new Date(datum.dueDate), 'LLL dd');

            return (
                <div className="rounded-md border border-border/60 bg-background px-3 py-2 text-xs shadow-md">
                    <p className="font-semibold">{datum.assignmentName}</p>
                    <p className="text-muted-foreground">{dueDateLabel}</p>
                    <p className="mt-1">
                        {t('teacher.correctRate')}: <span className="font-medium">{valueLabel}</span>
                    </p>
                </div>
            );
        },
        [t]
    );
    const hasAssignmentsInRange = assignmentsInRange.length > 0;
    const hasPendingAssignmentStats = React.useMemo(
        () => assignmentsInRange.some((entry) => !assignmentStatsCache[entry.key]),
        [assignmentsInRange, assignmentStatsCache]
    );
    const isPerformanceLoading = isAssignmentListLoading || (hasAssignmentsInRange && hasPendingAssignmentStats);

    const completionSummaryText = React.useMemo<string | null>(() => {
        if (isAssignmentListLoading || hasPendingAssignmentStats) {
            return t('teacher.assignmentSummaryLoading');
        }
        if (!hasAssignmentsInRange) {
            return null;
        }
        if (latestAssignmentPerformance) {
            return t('teacher.latestAssignmentSummary', {
                completed: latestAssignmentPerformance.completed,
                notCompleted: latestAssignmentPerformance.notCompleted,
            });
        }
        return t('teacher.assignmentDataPending');
    }, [hasAssignmentsInRange, hasPendingAssignmentStats, isAssignmentListLoading, latestAssignmentPerformance, t]);

    const correctnessSummaryText = React.useMemo(() => {
        if (isAssignmentListLoading || hasPendingAssignmentStats) {
            return t('teacher.assignmentSummaryLoading');
        }
        if (!hasAssignmentsInRange || !latestAssignmentPerformance) {
            return null;
        }
        if (!latestAssignmentPerformance.hasCompletedSubmissions) {
            return t('teacher.correctnessDataPending');
        }
        return t('teacher.recentAccuracyLabel', { value: `${(latestAssignmentPerformance.correctRate ?? 0).toFixed(1)}%` });
    }, [hasAssignmentsInRange, hasPendingAssignmentStats, isAssignmentListLoading, latestAssignmentPerformance, t]);

    const knowledgePointMistakes = React.useMemo<KnowledgePointMistakeStat[]>(() => {
        if (!assignmentsInRange.length) {
            return [];
        }

        const aggregated = new Map<string, { occurrences: number; incorrect: number }>();

        assignmentsInRange.forEach((entry) => {
            const questionStats = assignmentQuestionStatsCache[entry.key];
            if (!questionStats) {
                return;
            }

            questionStats.forEach((questionStat) => {
                const uniquePoints = Array.from(
                    new Set(
                        (questionStat.errorKnowledgePoints ?? [])
                            .map((point) => point?.trim())
                            .filter((point): point is string => Boolean(point))
                    )
                );
                if (!uniquePoints.length) {
                    return;
                }

                uniquePoints.forEach((point) => {
                    const record = aggregated.get(point) ?? { occurrences: 0, incorrect: 0 };
                    record.occurrences += 1;
                    if (questionStat.isFirstAttemptCorrect === false) {
                        record.incorrect += 1;
                    }
                    aggregated.set(point, record);
                });
            });
        });

        return Array.from(aggregated.entries())
            .map(([name, values]) => ({
                name,
                occurrences: values.occurrences,
                incorrect: values.incorrect,
                percentage: values.occurrences ? (values.incorrect / values.occurrences) * 100 : 0,
            }))
            .sort((a, b) => {
                const percentageDiff = b.percentage - a.percentage;
                if (percentageDiff !== 0) {
                    return percentageDiff;
                }
                const incorrectDiff = b.incorrect - a.incorrect;
                if (incorrectDiff !== 0) {
                    return incorrectDiff;
                }
                return a.name.localeCompare(b.name);
            })
            .slice(0, 3);
    }, [assignmentQuestionStatsCache, assignmentsInRange]);

    const handleQuestionDialogChange = React.useCallback((key: string, open: boolean) => {
        setDialogQuestionKey(open ? key : null);
    }, []);

    const questionMistakes = React.useMemo<QuestionMistakeStat[]>(() => {
        if (!assignmentsInRange.length) {
            return [];
        }

        const aggregated = new Map<string, QuestionMistakeStat>();

        assignmentsInRange.forEach((entry) => {
            const stats = assignmentStatsCache[entry.key];
            const questionStats = assignmentQuestionStatsCache[entry.key];
            if (!stats) {
                return;
            }

            // Build a map of (studentId, questionId) -> { isCorrect, answer } from QuestionStats
            const firstAttemptDataMap = new Map<string, { isCorrect: boolean; answer?: string }>();
            if (questionStats) {
                questionStats.forEach((qs) => {
                    if (qs.studentId != null && qs.id != null) {
                        const mapKey = `${qs.studentId}-${qs.id}`;
                        firstAttemptDataMap.set(mapKey, {
                            isCorrect: qs.isFirstAttemptCorrect === true,
                            answer: qs.firstAttemptAnswer,
                        });
                    }
                });
            }

            stats.forEach((studentStat) => {
                const studentName = studentStat.student?.name?.trim() || t('teacher.unknownStudent', { defaultValue: 'Unknown student' });
                const studentId = studentStat.student?.id;
                (studentStat.question ?? []).forEach((questionEntry) => {
                    if (!questionEntry) {
                        return;
                    }
                    const answered = questionEntry.isCompleted ?? Boolean(questionEntry.answer);
                    if (!answered) {
                        return;
                    }
                    const questionInfoId = questionEntry.questionInfoId ?? null;
                    const questionKeyBase = questionInfoId ?? questionEntry.id;
                    if (!questionKeyBase) {
                        return;
                    }
                    const key = questionInfoId ? `questionInfo-${questionInfoId}` : `question-${questionKeyBase}`;
                    const fallbackLabel = questionInfoId
                        ? t('common.question', { defaultValue: 'Question' }) + ` #${questionInfoId}`
                        : t('common.question', { defaultValue: 'Question' }) + ` #${questionKeyBase}`;
                    const existing = aggregated.get(key) ?? {
                        key,
                        questionInfoId,
                        fallbackLabel,
                        occurrences: 0,
                        incorrect: 0,
                        percentage: 0,
                        responses: [],
                    };
                    existing.occurrences += 1;

                    // Use firstAttemptAnswer and isFirstAttemptCorrect from QuestionStats
                    const questionId = questionEntry.id;
                    const firstAttemptKey = studentId != null && questionId != null ? `${studentId}-${questionId}` : null;
                    const firstAttemptData = firstAttemptKey ? firstAttemptDataMap.get(firstAttemptKey) : undefined;
                    // Fallback to isCorrectAnswer if no QuestionStats data available
                    const isCorrect = firstAttemptData?.isCorrect ?? Boolean(questionEntry.isCorrectAnswer);

                    if (!isCorrect) {
                        existing.incorrect += 1;
                    }
                    // Use firstAttemptAnswer if available, fallback to latest answer
                    const answerToDisplay = firstAttemptData?.answer ?? questionEntry.answer;
                    const normalizedAnswer = normalizeRichText(answerToDisplay);
                    const responseText = normalizedAnswer || t('teacher.noStudentAnswer', { defaultValue: 'No response provided.' });
                    existing.responses.push({
                        id: `${key}-${studentStat.student?.id ?? 'unknown'}-${existing.responses.length}`,
                        studentName,
                        answerText: responseText,
                        isCorrect: isCorrect,
                    });
                    aggregated.set(key, existing);
                });
            });
        });

        return Array.from(aggregated.values())
            .map((stat) => ({
                ...stat,
                percentage: stat.occurrences ? (stat.incorrect / stat.occurrences) * 100 : 0,
            }))
            .filter((stat) => stat.percentage > 0) // Exclude questions with 0% mistake rate
            .sort((a, b) => {
                const percentageDiff = b.percentage - a.percentage;
                if (percentageDiff !== 0) {
                    return percentageDiff;
                }
                const incorrectDiff = b.incorrect - a.incorrect;
                if (incorrectDiff !== 0) {
                    return incorrectDiff;
                }
                return a.fallbackLabel.localeCompare(b.fallbackLabel);
            })
            .slice(0, 3);
    }, [assignmentStatsCache, assignmentQuestionStatsCache, assignmentsInRange, t]);

    React.useEffect(() => {
        if (!questionMistakes.length) {
            return;
        }

        const missingIds = questionMistakes
            .map((item) => item.questionInfoId ?? null)
            .filter((id): id is number => Boolean(id) && !questionInfoCache[id] && !questionInfoFetchQueue.current.has(id));

        if (!missingIds.length) {
            return;
        }

        missingIds.forEach((id) => {
            questionInfoFetchQueue.current.add(id);
            getQuestionInfo({ questionId: id })
                .then((rsp) => {
                    if (rsp.code === 200 && rsp.data?.question) {
                        setQuestionInfoCache((prev) => ({
                            ...prev,
                            [id]: rsp.data!.question,
                        }));
                    }
                })
                .catch((error) => {
                    console.error('Error fetching question info', error);
                })
                .finally(() => {
                    questionInfoFetchQueue.current.delete(id);
                });
        });
    }, [questionInfoCache, questionMistakes]);

    const resolveQuestionTexts = React.useCallback(
        (questionInfoId?: number | null): QuestionTextResolution => {
            if (!questionInfoId) {
                return { preview: '', fullText: '', status: 'missing' };
            }
            const info = questionInfoCache[questionInfoId];
            if (!info) {
                return { preview: '', fullText: '', status: 'loading' };
            }
            const normalized = normalizeRichText(getQuestionText(info, i18n));
            if (!normalized) {
                return { preview: '', fullText: '', status: 'empty' };
            }
            const preview = buildPreviewText(normalized);
            return {
                preview,
                fullText: normalized,
                status: 'ready',
            };
        },
        [i18n, questionInfoCache]
    );
    React.useEffect(() => {
        if (!dialogQuestionKey) {
            return;
        }
        if (!questionMistakes.some((item) => item.key === dialogQuestionKey)) {
            setDialogQuestionKey(null);
        }
    }, [dialogQuestionKey, questionMistakes]);

    const isMistakeSummaryLoading =
        isAssignmentListLoading || (hasAssignmentsInRange && (hasPendingAssignmentStats || isQuestionStatsLoading));
    const hasMistakeData = knowledgePointMistakes.length > 0 || questionMistakes.length > 0;

    return (
        <ContentLayout title={t('teacher.studentoverview')}>
            <div className="mx-auto space-y-6 py-6 bg-background/80 mt-3">
                <div className="px-8 flex flex-wrap gap-4 items-center px-4">
                    <ClassFilterSelect
                        classes={classOptions}
                        value={selectedClassId}
                        onChange={setSelectedClassId}
                        placeholder={t('common.class')}
                        triggerClassName="w-[200px] bg-muted"
                    />
                    <Select value={selectedSource} onValueChange={setSelectedSource}>
                        <SelectTrigger className="w-[200px] bg-muted">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('teacher.sourceAll')}</SelectItem>
                            <SelectItem value="own">{t('teacher.sourceOwn')}</SelectItem>
                            <SelectItem value="shared">{t('teacher.sourceShared')}</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="flex flex-wrap items-center gap-3">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button id="date-from" variant="outline" className={cn('w-[200px] justify-start text-left font-normal bg-muted')}>
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    <div className="flex flex-col items-start">
                                        <span className="text-xs text-muted-foreground">
                                            {t('common.startDate', { defaultValue: 'Start Date' })}
                                        </span>
                                        <span>
                                            {date.from
                                                ? format(date.from, 'LLL dd, y', { locale: isEN(i18n) ? enUS : zhHK })
                                                : t('common.selectDate', { defaultValue: 'Select date' })}
                                        </span>
                                    </div>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar initialFocus mode="single" defaultMonth={date?.from} selected={date.from} onSelect={handleStartDateSelect} />
                            </PopoverContent>
                        </Popover>
                        <span className="text-muted-foreground">-</span>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button id="date-to" variant="outline" className={cn('w-[200px] justify-start text-left font-normal bg-muted')}>
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    <div className="flex flex-col items-start">
                                        <span className="text-xs text-muted-foreground">
                                            {t('common.endDate', { defaultValue: 'End Date' })}
                                        </span>
                                        <span>
                                            {date.to
                                                ? format(date.to, 'LLL dd, y', { locale: isEN(i18n) ? enUS : zhHK })
                                                : t('common.selectDate', { defaultValue: 'Select date' })}
                                        </span>
                                    </div>
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    initialFocus
                                    mode="single"
                                    defaultMonth={date?.to ?? date?.from}
                                    selected={date.to}
                                    onSelect={handleEndDateSelect}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <div className="mx-4 bg-background rounded-lg">
                    <div className="grid grid-cols-2 gap-6 p-4">
                        <Card className="flex flex-col bg-muted">
                            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-0">
                                <CardTitle className="font-bold text-lg">{t('common.completestatus')}</CardTitle>
                                {completionSummaryText && (
                                    <p className="text-base text-right text-muted-foreground">{completionSummaryText}</p>
                                )}
                            </CardHeader>
                            <CardContent className="flex-1 pt-4">
                                {isPerformanceLoading ? (
                                    <p className="text-sm text-muted-foreground">
                                        {t('common.loading', { defaultValue: 'Loading...' })}
                                    </p>
                                ) : assignmentPerformanceData.length > 0 ? (
                                    <>
                                        <ChartContainer config={chartConfig} className="w-full">
                                            <BarChart
                                                accessibilityLayer
                                                data={assignmentPerformanceData}
                                                margin={{ left: 16, right: 32 }}
                                            >
                                                <CartesianGrid vertical={false} strokeDasharray="4 4" />
                                                <XAxis
                                                    dataKey="assignmentName"
                                                    tickLine={false}
                                                    tickMargin={10}
                                                    axisLine={false}
                                                    interval={0}
                                                    height={60}
                                                    padding={{ left: 24, right: 24 }}
                                                    tick={<AssignmentAxisTick />}
                                                />
                                                <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
                                                <Tooltip
                                                    formatter={(value: number | string, name) => {
                                                        const displayValue = typeof value === 'number' ? value.toString() : value;
                                                        return [displayValue, name === 'completed' ? t('common.completed') : t('common.notCompleted')];
                                                    }}
                                                    labelFormatter={(value, payload) => {
                                                        const datum = payload?.[0]?.payload as AssignmentPerformanceDatum | undefined;
                                                        if (!datum) {
                                                            return value as string;
                                                        }
                                                        const dueDateLabel = format(new Date(datum.dueDate), 'LLL dd');
                                                        return `${value} · ${dueDateLabel}`;
                                                    }}
                                                />
                                                <Bar dataKey="completed" stackId="completion" fill="var(--color-assignmentCompleted)" />
                                                <Bar dataKey="notCompleted" stackId="completion" fill="var(--color-assignmentNotCompleted)" />
                                            </BarChart>
                                        </ChartContainer>
                                        <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
                                            <div className="flex items-center gap-2">
                                                <span className="h-2 w-6 rounded-full" style={{ backgroundColor: NOT_COMPLETED_COLOR }} />
                                                <span>{t('common.notCompleted')}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="h-2 w-6 rounded-full" style={{ backgroundColor: COMPLETED_COLOR }} />
                                                <span>{t('common.completed')}</span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        {hasAssignmentsInRange ? t('teacher.assignmentDataPending') : t('teacher.noAssignmentDataForClass')}
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="flex flex-col bg-muted">
                            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-0">
                                <CardTitle className="font-bold text-lg">{t('teacher.homeworkCorrectnessOverview')}</CardTitle>
                                {correctnessSummaryText && (
                                    <span className="text-base text-right text-muted-foreground">{correctnessSummaryText}</span>
                                )}
                            </CardHeader>
                            <CardContent className="flex-1 pt-4">
                                {isPerformanceLoading ? (
                                    <p className="text-sm text-muted-foreground">
                                        {t('common.loading', { defaultValue: 'Loading...' })}
                                    </p>
                                ) : assignmentPerformanceData.length > 0 ? (
                                    <>
                                        <ChartContainer config={chartConfig} className="w-full">
                                            <LineChart
                                                accessibilityLayer
                                                data={assignmentPerformanceData}
                                                margin={{ left: 16, right: 32 }}
                                            >
                                                <CartesianGrid vertical={false} strokeDasharray="4 4" />
                                                <XAxis
                                                    dataKey="assignmentName"
                                                    tickLine={false}
                                                    axisLine={false}
                                                    interval={0}
                                                    height={60}
                                                    padding={{ left: 24, right: 24 }}
                                                    tick={<AssignmentAxisTick />}
                                                />
                                                <YAxis
                                                    type="number"
                                                    domain={[0, 100]}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    tickFormatter={(value) => `${value.toFixed(0)}%`}
                                                />
                                                <Tooltip content={renderCorrectnessTooltip} />
                                                <Line
                                                    type="linear"
                                                    dataKey="solidLineValue"
                                                    stroke="var(--color-assignmentCorrectRate)"
                                                    strokeWidth={3}
                                                    dot={renderDot}
                                                    activeDot={(props) => renderDot({ ...props, active: true })}
                                                    connectNulls={false}
                                                />
                                                <Line
                                                    type="linear"
                                                    dataKey="dottedLineValue"
                                                    stroke="var(--color-assignmentMissing)"
                                                    strokeWidth={2}
                                                    strokeDasharray="4 4"
                                                    dot={false}
                                                    activeDot={false}
                                                    connectNulls={false}
                                                />
                                                <Line
                                                    type="linear"
                                                    dataKey="naPointValue"
                                                    stroke="transparent"
                                                    dot={renderDot}
                                                    activeDot={(props) => renderDot({ ...props, active: true })}
                                                    connectNulls={false}
                                                />
                                                <Line
                                                    type="linear"
                                                    dataKey="submissionDotValue"
                                                    stroke="transparent"
                                                    dot={renderDot}
                                                    activeDot={(props) => renderDot({ ...props, active: true })}
                                                    connectNulls={false}
                                                />
                                            </LineChart>
                                        </ChartContainer>
                                        <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
                                            <div className="flex items-center gap-2">
                                                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: CORRECTNESS_PASS_DOT_COLOR }} />
                                                <span>{t('teacher.correctnessLegendPass')}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: CORRECTNESS_FAIL_COLOR }} />
                                                <span>{t('teacher.correctnessLegendFail')}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="h-3 w-3 rounded-full border"
                                                    style={{
                                                        backgroundColor: NA_POINT_FILL,
                                                        borderColor: NA_POINT_STROKE,
                                                        borderStyle: 'dashed',
                                                        borderWidth: '2px',
                                                    }}
                                                />
                                                <span>{t('teacher.correctnessLegendNoData', { defaultValue: 'No submissions' })}</span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        {hasAssignmentsInRange ? t('teacher.correctnessDataPending') : t('teacher.noAssignmentDataForClass')}
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="flex flex-col bg-muted">
                            {/* Mistake Summary Panel */}
                            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-0">
                                <CardTitle className="font-bold text-lg">{t('common.wrongly')}</CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 pt-4">
                                {isMistakeSummaryLoading ? (
                                    <p className="text-sm text-muted-foreground">{t('teacher.assignmentSummaryLoading')}</p>
                                ) : !hasAssignmentsInRange ? (
                                    <p className="text-sm text-muted-foreground">{t('teacher.noAssignmentDataForClass')}</p>
                                ) : !hasMistakeData ? (
                                    <p className="text-sm text-muted-foreground">{t('teacher.insightsEmpty')}</p>
                                ) : (
                                    <div className="flex-1 w-full space-y-6">
                                        <div>
                                            <h4 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
                                                {t('teacher.topMistakedKnowledgePoints')}
                                            </h4>
                                            {knowledgePointMistakes.length ? (
                                                <ul className="mt-3 space-y-2">
                                                    {knowledgePointMistakes.map((item) => {
                                                        const knowledgePointLabel = item.name || t('teacher.unknownKnowledgePoint');
                                                        return (
                                                            <li
                                                                key={item.name}
                                                                className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-background border border-border/60 px-3 py-2 text-sm"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <span
                                                                        className="text-lg font-semibold"
                                                                        style={{
                                                                            color:
                                                                                item.percentage >= 60
                                                                                    ? CORRECTNESS_FAIL_COLOR
                                                                                    : CORRECTNESS_PASS_DOT_COLOR,
                                                                        }}
                                                                    >{`${Math.round(item.percentage)}%`}</span>
                                                                    <span className="font-medium">[{knowledgePointLabel}]</span>
                                                                </div>
                                                                <span className="text-xs text-muted-foreground">
                                                                    {t('teacher.knowledgePointIncorrectLabel', {
                                                                        count: item.incorrect,
                                                                    })}
                                                                </span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : (
                                                <p className="mt-2 text-sm text-muted-foreground">{t('teacher.insightsEmpty')}</p>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="text-base font-semibold uppercase tracking-wide text-muted-foreground">
                                                {t('teacher.topMistakedQuestions')}
                                            </h4>
                                            {questionMistakes.length ? (
                                                <ul className="mt-3 space-y-3">
                                                    {questionMistakes.map((item) => {
                                                        const { preview, fullText, status } = resolveQuestionTexts(item.questionInfoId);
                                                        const hasDetails = status === 'ready' && Boolean(fullText);
                                                        const questionLabel =
                                                            status === 'ready'
                                                                ? preview
                                                                : status === 'empty'
                                                                    ? t('teacher.noQuestionText')
                                                                    : item.fallbackLabel;
                                                        return (
                                                            <li key={item.key} className="rounded-md bg-background border border-border/60 p-3 text-sm">
                                                                <div className="flex flex-wrap items-center gap-3">
                                                                    <span
                                                                        className="text-lg font-semibold"
                                                                        style={{
                                                                            color:
                                                                                item.percentage >= 60
                                                                                    ? CORRECTNESS_FAIL_COLOR
                                                                                    : CORRECTNESS_PASS_DOT_COLOR,
                                                                        }}
                                                                    >{`${Math.round(item.percentage)}%`}</span>
                                                                    <div className="flex-1 text-muted-foreground text-sm">
                                                                        <ReadonlyQuestionDisplay
                                                                            questionText={questionLabel}
                                                                            className="w-full whitespace-pre-wrap break-words"
                                                                        />
                                                                    </div>
                                                                    {hasDetails && (
                                                                        <Dialog
                                                                            open={dialogQuestionKey === item.key}
                                                                            onOpenChange={(open) => handleQuestionDialogChange(item.key, open)}
                                                                        >
                                                                            <DialogTrigger asChild>
                                                                                <Button variant="link" size="sm" className="px-0">
                                                                                    {t('teacher.seeDetails')}
                                                                                </Button>
                                                                            </DialogTrigger>
                                                                            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden bg-muted">
                                                                                <DialogHeader className="text-left">
                                                                                    <DialogTitle className="text-base font-semibold">
                                                                                        {item.fallbackLabel}
                                                                                    </DialogTitle>
                                                                                    <DialogDescription className="text-sm text-muted-foreground max-h-48 overflow-y-auto">
                                                                                        <ReadonlyQuestionDisplay
                                                                                            questionText={fullText}
                                                                                            className="whitespace-pre-wrap break-words text-sm text-muted-foreground"
                                                                                        />
                                                                                    </DialogDescription>
                                                                                </DialogHeader>
                                                                                {item.responses.length ? (
                                                                                    <div className="mt-4">
                                                                                        <p className="text-sm font-semibold">{t('teacher.studentResponses', { defaultValue: 'Student responses' })}</p>
                                                                                        <ScrollArea className="mt-2 h-64 pr-1">
                                                                                            <div className="space-y-2">
                                                                                                {item.responses.map((response) => (
                                                                                                    <div
                                                                                                        key={response.id}
                                                                                                        className="rounded-md border border-border/60 px-3 py-2 text-sm bg-accent"
                                                                                                    >
                                                                                                        <div className="flex items-center justify-between gap-2">
                                                                                                            <span className="font-medium">
                                                                                                                {response.studentName}
                                                                                                            </span>
                                                                                                            <span
                                                                                                                className={cn(
                                                                                                                    'text-xs font-semibold',
                                                                                                                    response.isCorrect
                                                                                                                        ? 'text-emerald-600'
                                                                                                                        : 'text-red-500'
                                                                                                                )}
                                                                                                            >
                                                                                                                {response.isCorrect ? t('common.correct', { defaultValue: 'Correct' }) : t('common.wrong', { defaultValue: 'Wrong' })}
                                                                                                            </span>
                                                                                                        </div>
                                                                                                        <ReadonlyQuestionDisplay
                                                                                                            questionText={response.answerText}
                                                                                                            className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap break-words"
                                                                                                        />
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        </ScrollArea>
                                                                                    </div>
                                                                                ) : (
                                                                                    <p className="mt-4 text-sm text-muted-foreground">
                                                                                        {t('teacher.noStudentResponses', { defaultValue: 'No student responses available.' })}
                                                                                    </p>
                                                                                )}
                                                                            </DialogContent>
                                                                        </Dialog>
                                                                    )}
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : (
                                                <p className="mt-2 text-sm text-muted-foreground">{t('teacher.insightsEmpty')}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <Card className="flex flex-col bg-muted">
                            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-0">
                                <CardTitle className="font-bold text-lg">{t('teacher.AISummary')}</CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 pt-4">
                                <InsightsChat />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </ContentLayout>
    );
};

export default OverviewPage;
